import * as Sentry from '@sentry/react';
import mixpanel from 'mixpanel-browser';
import 'polyfill';
import React from 'react';
import ReactDOM from 'react-dom';
import { createRoot } from 'react-dom/client';
import 'typeface-cabin';
import 'typeface-lato';
import 'typeface-quicksand';
import App from './App';
import Config from './config';
import './index.css';
import * as serviceWorker from './serviceWorker';

if (Config.SENTRY_DSN) {
  const eventLimiter: { [key: string]: boolean } = {};
  Sentry.init({
    dsn: Config.SENTRY_DSN,
    release: Config.COMMIT,
    environment: Config.APP_ENV,
    sampleRate: 0.25,
    integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
    tracesSampleRate: 1,
    tracePropagationTargets: ['https://app.stayqrious.com/api/'],
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1,
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      /getReadModeConfig/,
      /getReadModeRender/,
      /getReadModeExtract/,
    ],
    beforeSend: (event, hint) => {
      const msg = (hint?.originalException as Error | undefined)?.message as string;

      // do not send event if already sent in last 1 minute
      if (msg && msg in eventLimiter) {
        console.log('Rate limiting activated for', msg);
        return null;
      }

      eventLimiter[msg] = true;

      setTimeout(() => {
        delete eventLimiter[msg];
      }, 300 * 1000); // 5 minutes?

      return event;
    },
    denyUrls: [/^file:\/\//],
  });
}

if (Config.MIXPANEL_TOKEN) {
  mixpanel.init(Config.MIXPANEL_TOKEN);
} else if (Config.APP_ENV === 'mock') {
  // Mock mode has no real token, but code paths like UserStore.pullUser() call
  // mixpanel.identify()/track() unconditionally. An uninitialized mixpanel throws
  // ("Cannot read properties of undefined (reading 'before_identify')"), which
  // rejects pullUser() and shows "App failed to load". Initialize with a stub
  // token and opt out of tracking so those calls are safe no-ops with no network.
  mixpanel.init('mock', { opt_out_tracking_by_default: true });
}

/**
 * Reading `localStorage` throws a SecurityError when the document is sandboxed
 * or cross-origin — which happens for embedded frames — and at bootstrap that
 * surfaces as an uncaught error on every page load. It is harmless in itself,
 * but it buries genuine startup failures, so the access is guarded here rather
 * than left to the top-level handler.
 */
function readE2EMockDailyFlag(): boolean {
  try {
    return window.localStorage.getItem('e2eMockDaily') === 'true';
  } catch {
    return false;
  }
}

async function bootstrap() {
  // Start mock service worker in mock mode (REACT_APP_MOCK=true)
  if (process.env.REACT_APP_MOCK === 'true') {
    try {
      const { startMocks } = await import('./mocks/browser');
      await startMocks();
    } catch (err) {
      console.error('[bootstrap] Mock startup failed, continuing without mocks:', err);
    }
  } else if (process.env.NODE_ENV === 'development' && readE2EMockDailyFlag()) {
    // Live-backend e2e sessions (real API, real RT websocket) run in sandboxes
    // that cannot reach Daily's media servers, and a failed call join blanks
    // every conference station behind CallRoomSuspense. This flag mocks ONLY
    // the Daily call layer so classroom content flows stay fully real.
    // Set by tests/live/ before page load, against the dev server only: the
    // NODE_ENV guard is inlined at build time, so production bundles drop
    // both this branch and the mock chunk, and production users cannot
    // activate the mock via localStorage.
    try {
      const { installMockDailyco } = await import('./mocks/mockDailyco');
      installMockDailyco();
    } catch (err) {
      console.error('[bootstrap] e2eMockDaily install failed, continuing with real Daily:', err);
    }
  }

  const container = document.getElementById('root');
  const root = createRoot(container!);
  root.render(<App />);
}

bootstrap().catch((err) => {
  console.error('[bootstrap] Fatal error during app initialization:', err);
});

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();

declare global {
  interface Window {
    React: any;
    ReactDOM: any;
    withStyles: any;
  }
}

//// export
window.React = React;
window.ReactDOM = ReactDOM;
